import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, useNavigate, Link } from 'react-router-dom';
import { ShoppingBag, ArrowLeft } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import DataTable from '../../components/common/DataTable';
import SearchInput from '../../components/common/SearchInput';
import Select from '../../components/common/Select';
import StatusBadge from '../../components/common/StatusBadge';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import { useAuth } from '../../hooks/useAuth';
import { orderService } from '../../services/orderService';
import { formatPrice } from '../../utils/priceCalculator';
import { formatDateTime } from '../../utils/formatRelativeTime';

const PAGE_SIZE = 20;

const ClientOrders = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orderService.getAll({
        status: filter || undefined,
        page,
        limit: PAGE_SIZE,
      });
      setRows(data?.rows || []);
      setTotal(data?.count || 0);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading) return <Skeleton variant="block" height={120} />;
  if (!isAuthenticated) return <Navigate to="/entrar" replace />;

  const filtered = q ? rows.filter((o) => `${o.id}`.includes(q)) : rows;

  const columns = [
    {
      key: 'id',
      header: 'Pedido',
      width: 100,
      render: (o) => <span className="font-mono text-sm">#{o.id}</span>,
    },
    {
      key: 'store',
      header: 'Loja',
      render: (o) => <span className="text-sm">{o.Store?.name || '—'}</span>,
    },
    {
      key: 'createdAt',
      header: 'Data',
      render: (o) => (
        <span className="text-sm text-app-text-secondary">{formatDateTime(o.createdAt)}</span>
      ),
    },
    {
      key: 'items',
      header: 'Itens',
      align: 'center',
      render: (o) => <span className="text-sm">{(o.items || []).length}</span>,
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (o) => (
        <span className="font-semibold">{formatPrice(parseFloat(o.totalToPay || 0))}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (o) => <StatusBadge domain="order" status={o.status} />,
    },
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <Link
        to="/catalogo"
        className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline mb-3"
      >
        <ArrowLeft size={14} /> Voltar à loja
      </Link>
      <h1 className="text-3xl font-extrabold text-app-text mb-6">Meus pedidos</h1>

      <Card className="p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <SearchInput value={q} onChange={setQ} placeholder="Buscar por #ID…" />
          </div>
          <Select
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: '', label: 'Todos status' },
              { value: 'pending', label: 'Pendente' },
              { value: 'awaiting_approval', label: 'Aguardando aprovação' },
              { value: 'approved', label: 'Aprovado' },
              { value: 'paid', label: 'Pago' },
              { value: 'shipped', label: 'Enviado' },
              { value: 'delivered', label: 'Entregue' },
              { value: 'rejected', label: 'Recusado' },
              { value: 'cancelled', label: 'Cancelado' },
            ]}
          />
        </div>
      </Card>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        total={q ? filtered.length : total}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        onRowClick={(o) => navigate(`/minha-conta/pedidos/${o.id}`)}
        empty={
          <EmptyState
            icon={ShoppingBag}
            title={filter || q ? 'Nada por aqui' : 'Você ainda não comprou'}
            description={filter || q ? 'Ajuste filtros.' : 'Comece explorando o catálogo.'}
            action={
              !filter && !q && (
                <Link to="/catalogo">
                  <Button>Ver catálogo</Button>
                </Link>
              )
            }
          />
        }
      />
    </div>
  );
};

export default ClientOrders;
